import { __ } from '@wordpress/i18n';
import { 
    BaseControl,
    Button,
    ButtonGroup,
    SelectControl,
    RangeControl,
    ToggleControl,
    TabPanel,
    PanelBody
} from '@wordpress/components';
import FlatColorPicker from '../FlatColorPicker';

const Typography = ({ 
    value = {}, 
    onChange,
    label = __('Tipografi', 'kanews'),
    showFontFamily = false,
    showFontSize = true,
    showFontWeight = true,
    showLineHeight = true,
    showLetterSpacing = true,
    showTextTransform = true,
    showFontStyle = true,
    showTextDecoration = true,
    showTextAlign = true,
    showColor = true,
    showReset = true
}) => {
    const {
        fontFamily = '',
        fontSize = '',
        fontWeight = 'normal',
        lineHeight = '',
        letterSpacing = '',
        textTransform = 'none',
        fontStyle = 'normal',
        textDecoration = 'none',
        textAlign = 'left',
        color = ''
    } = value;

    const handleChange = (property, newValue) => {
        onChange({
            ...value,
            [property]: newValue
        });
    };

    const handleReset = () => {
        onChange({
            fontFamily: '',
            fontSize: '',
            fontWeight: 'normal',
            lineHeight: '',
            letterSpacing: '',
            textTransform: 'none',
            fontStyle: 'normal',
            textDecoration: 'none',
            textAlign: 'left',
            color: ''
        });
    };

    const tabs = [
        {
            name: 'basic',
            title: __('Temel', 'kanews'),
            className: 'tab-basic'
        },
        {
            name: 'advanced',
            title: __('Gelişmiş', 'kanews'),
            className: 'tab-advanced'
        }
    ];

    const renderBasicControls = () => (
        <>
            {showFontFamily && (
                <div className="kan-control-item">
                    <label className="kan-control-label">
                        {__('Yazı Tipi', 'kanews')}
                    </label>
                    <SelectControl
                        value={fontFamily}
                        options={[
                            { label: __('Varsayılan', 'kanews'), value: '' },
                            { label: 'Arial', value: 'Arial, sans-serif' },
                            { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
                            { label: 'Georgia', value: 'Georgia, serif' },
                            { label: 'Times New Roman', value: 'Times New Roman, serif' },
                            { label: 'Verdana', value: 'Verdana, sans-serif' },
                        ]}
                        onChange={(newValue) => handleChange('fontFamily', newValue)}
                    />
                </div>
            )}

            {showFontSize && (
                <div className="kan-control-item">
                    <label className="kan-control-label">
                        {__('Yazı Boyutu', 'kanews')} <span className="kan-control-unit">(px)</span>
                    </label>
                    <RangeControl
                        value={fontSize ? parseInt(fontSize) : ''}
                        onChange={(newValue) => handleChange('fontSize', newValue ? `${newValue}px` : '')}
                        min={8}
                        max={100}
                        step={1}
                        withInputField={true}
                    />
                </div>
            )}

            {showFontWeight && (
                <div className="kan-control-item">
                    <label className="kan-control-label">
                        {__('Yazı Kalınlığı', 'kanews')}
                    </label>
                    <SelectControl
                        value={fontWeight}
                        options={[
                            { label: __('Varsayılan', 'kanews'), value: '' },
                            { label: '500', value: '500' },
                            { label: '600', value: '600' },
                            { label: '700', value: '700' },
                        ]}
                        onChange={(newValue) => handleChange('fontWeight', newValue)}
                    />
                </div>
            )}

            {showColor && (
                <div className="kan-control-item">
                    <label className="kan-control-label">
                        {__('Metin Rengi', 'kanews')}
                    </label>
                    <FlatColorPicker
                        value={color}
                        onChange={(newValue) => handleChange('color', newValue)}
                    />
                </div>
            )}

            {showTextAlign && (
                <div className="kan-control-item">
                    <label className="kan-control-label">
                        {__('Metin Hizalama', 'kanews')}
                    </label>
                    <ButtonGroup>
                        <Button
                            isSmall
                            isPrimary={textAlign === 'left'}
                            onClick={() => handleChange('textAlign', 'left')}
                            icon="editor-alignleft"
                        />
                        <Button
                            isSmall
                            isPrimary={textAlign === 'center'}
                            onClick={() => handleChange('textAlign', 'center')}
                            icon="editor-aligncenter"
                        />
                        <Button
                            isSmall
                            isPrimary={textAlign === 'right'}
                            onClick={() => handleChange('textAlign', 'right')}
                            icon="editor-alignright"
                        />
                        <Button
                            isSmall
                            isPrimary={textAlign === 'justify'}
                            onClick={() => handleChange('textAlign', 'justify')}
                            icon="editor-justify"
                        />
                    </ButtonGroup>
                </div>
            )}
        </>
    );

    const renderAdvancedControls = () => (
        <>
            {showLineHeight && (
                <div className="kan-control-item">
                    <label className="kan-control-label">
                        {__('Satır Yüksekliği', 'kanews')} <span className="kan-control-unit">(em)</span>
                    </label>
                    <RangeControl
                        value={lineHeight ? parseFloat(lineHeight) : 1.5}
                        onChange={(newValue) => handleChange('lineHeight', newValue)}
                        min={1}
                        max={3}
                        step={0.1}
                        withInputField={true}
                    />
                </div>
            )}

            {showLetterSpacing && (
                <div className="kan-control-item">
                    <label className="kan-control-label">
                        {__('Harf Aralığı', 'kanews')} <span className="kan-control-unit">(px)</span>
                    </label>
                    <RangeControl
                        value={letterSpacing ? parseInt(letterSpacing) : 0}
                        onChange={(newValue) => handleChange('letterSpacing', newValue ? `${newValue}px` : '')}
                        min={-5}
                        max={20}
                        step={1}
                        withInputField={true}
                    />
                </div>
            )}

            {showTextTransform && (
                <div className="kan-control-item">
                    <label className="kan-control-label">
                        {__('Metin Dönüşümü', 'kanews')}
                    </label>
                    <SelectControl
                        value={textTransform}
                        options={[
                            { label: __('Normal', 'kanews'), value: 'none' },
                            { label: __('Büyük Harf', 'kanews'), value: 'uppercase' },
                            { label: __('Küçük Harf', 'kanews'), value: 'lowercase' },
                            { label: __('Baş Harfler Büyük', 'kanews'), value: 'capitalize' }
                        ]}
                        onChange={(newValue) => handleChange('textTransform', newValue)}
                    />
                </div>
            )}

            {showFontStyle && (
                <div className="kan-control-item">
                    <label className="kan-control-label">
                        {__('İtalik', 'kanews')}
                    </label>
                    <ToggleControl
                        checked={fontStyle === 'italic'}
                        onChange={(isChecked) => handleChange('fontStyle', isChecked ? 'italic' : 'normal')}
                    />
                </div>
            )}

            {showTextDecoration && (
                <div className="kan-control-item">
                    <label className="kan-control-label">
                        {__('Metin Dekorasyonu', 'kanews')}
                    </label>
                    <SelectControl
                        value={textDecoration}
                        options={[
                            { label: __('Normal', 'kanews'), value: 'none' },
                            { label: __('Altı Çizili', 'kanews'), value: 'underline' },
                            { label: __('Üstü Çizili', 'kanews'), value: 'overline' },
                            { label: __('Üstü ve Altı Çizili', 'kanews'), value: 'underline overline' }
                        ]}
                        onChange={(newValue) => handleChange('textDecoration', newValue)}
                    />
                </div>
            )}
        </>
    );

    return (
        <PanelBody
            title={label}
            initialOpen={true}
            className="kan-control-panel"
        >
            <div className="kan-control-item kan-spacing-control">
            {showReset && (
                            <Button
                                isSmall
                                isSecondary
                                onClick={handleReset}
                                className="kan-reset-button"
                                text={__('Sıfırla', 'kanews')}
                            />
                        )}

            <TabPanel
                className="kan-typography-tabs"
                activeClass="is-active"
                tabs={tabs}
            >
                {(tab) => (
                    <div className={`kan-typography-tab-content ${tab.name}`}>
                        {tab.name === 'basic' ? renderBasicControls() : renderAdvancedControls()}
                    </div>
                )}
            </TabPanel>
            </div>
        </PanelBody>
    );
};

export default Typography; 